import { Tooltip } from '@nextui-org/react'
import React, { useRef } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '~/components/common/dropdowns/DropdownMenu'
import { Typography } from '~/components/common/Typography'

// Maximum number of files that can be uploaded
export const MAX_CONTEXT_UPLOAD_FILES = 10

// Icons for the attach upload dropdown
const AttachPlusIcon = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M8 3.5V12.5M3.5 8H12.5'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

/** Shared with landing (+) menu — uses `currentColor` for design-token text/icon classes on the parent. */
export function UploadFileIcon() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      aria-hidden
      className='border-0'
    >
      <path
        className='fill-tokens-neutral-bg-white'
        d='M9.33333 1.33334H4C3.64638 1.33334 3.30724 1.47382 3.05719 1.72387C2.80714 1.97392 2.66667 2.31305 2.66667 2.66667V13.3333C2.66667 13.687 2.80714 14.0261 3.05719 14.2762C3.30724 14.5262 3.64638 14.6667 4 14.6667H12C12.3536 14.6667 12.6928 14.5262 12.9428 14.2762C13.1929 14.0261 13.3333 13.687 13.3333 13.3333V5.33334M9.33333 1.33334L13.3333 5.33334M9.33333 1.33334V5.33334H13.3333M10.6667 8.66667H5.33333M10.6667 11.3333H5.33333M6.66667 6H5.33333'
        stroke='currentColor'
        strokeWidth='1.2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

/** Shared with landing (+) menu — uses `currentColor` for design-token text/icon classes on the parent. */
export function UploadFolderIcon() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      aria-hidden
      className='border-0'
    >
      <path
        d='M14.6667 12.6667C14.6667 13.0203 14.5262 13.3594 14.2761 13.6095C14.0261 13.8595 13.687 14 13.3333 14H2.66667C2.31304 14 1.97391 13.8595 1.72386 13.6095C1.47381 13.3594 1.33333 13.0203 1.33333 12.6667V3.33333C1.33333 2.97971 1.47381 2.64057 1.72386 2.39052C1.97391 2.14048 2.31304 2 2.66667 2H6L7.33333 4H13.3333C13.687 4 14.0261 4.14048 14.2761 4.39052C14.5262 4.64057 14.6667 4.97971 14.6667 5.33333V12.6667Z'
        stroke='currentColor'
        strokeWidth='1.2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right'
export interface AttachUploadButtonProps {
  onFilesSelect: (files: FileList) => void
  /** Unified folder select handler (works in both browser and plugin) */
  onFolderSelect: () => void
  disabled?: boolean
  /** Current number of uploaded files (used for max limit) */
  currentUploadCount?: number
  /** Tooltip content to show on hover */
  tooltipContent?: React.ReactNode
  /** Tooltip placement */
  tooltipPlacement?: TooltipPlacement
  /** Whether to show the folder upload option */
  showFolderOption?: boolean
}

export const AttachUploadButton: React.FC<AttachUploadButtonProps> = ({
  onFilesSelect,
  onFolderSelect,
  disabled = false,
  currentUploadCount = 0,
  tooltipContent,
  tooltipPlacement = 'top',
  showFolderOption = true
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const isMaxFilesReached = currentUploadCount >= MAX_CONTEXT_UPLOAD_FILES

  const handleFilesClick = () => {
    if (!isMaxFilesReached) {
      fileInputRef.current?.click()
    }
  }

  const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      onFilesSelect(files)
    }
    e.target.value = ''
  }

  // Radix's DropdownMenuTrigger (asChild) listens to pointerdown on the trigger
  // wrapper, which fires before a child button's native `disabled` blocks click —
  // so a disabled trigger still opens the menu. Render a plain disabled button
  // instead of mounting the menu when disabled.
  const triggerButtonClassName =
    'flex items-center justify-center w-8 h-8 rounded-xl border border-tokens-neutral-border-2 bg-white hover:bg-tokens-neutral-bg-grey-1 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white'

  const dropdownElement = disabled ? (
    <button
      type='button'
      className={triggerButtonClassName}
      disabled
      aria-label={showFolderOption ? 'Attach files or folder' : 'Attach files'}
    >
      <AttachPlusIcon />
    </button>
  ) : (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type='button'
          className={triggerButtonClassName}
          aria-label={showFolderOption ? 'Attach files or folder' : 'Attach files'}
        >
          <AttachPlusIcon />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='start' className='w-[161px] space-y-0'>
        {isMaxFilesReached ? (
          // Disabled state when max files reached
          <div
            className='flex items-center justify-start gap-0 px-2 py-1.5 cursor-not-allowed'
            title={`Maximum ${MAX_CONTEXT_UPLOAD_FILES} files allowed`}
          >
            <span className='flex items-center justify-center w-8 h-8 shrink-0 text-tokens-neutral-text-quaternary'>
              <UploadFileIcon />
            </span>
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              fontWeight='normal'
              className='text-tokens-neutral-text-quaternary grow text-left'
            >
              Max {MAX_CONTEXT_UPLOAD_FILES} files
            </Typography>
          </div>
        ) : (
          <DropdownMenuItem onClick={handleFilesClick} className='justify-start gap-0 pl-1 pr-0'>
            <span className='flex items-center justify-center w-[32px] h-8 shrink-0 text-tokens-neutral-icon-hover'>
              <UploadFileIcon />
            </span>
            <span className='text-tokens-neutral-text-primary grow text-left'>Upload files</span>
          </DropdownMenuItem>
        )}
        {showFolderOption && (
          <DropdownMenuItem onClick={onFolderSelect} className='justify-start gap-0 pl-1 pr-0'>
            <span className='flex items-center justify-center w-[32px] h-8 shrink-0 text-tokens-neutral-icon-hover'>
              <UploadFolderIcon />
            </span>
            <span className='text-tokens-neutral-text-primary grow text-left'>Upload folder</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  return (
    <>
      {tooltipContent ? (
        <Tooltip
          content={tooltipContent}
          placement={tooltipPlacement}
          showArrow
          delay={0}
          closeDelay={0}
          classNames={{
            base: 'max-w-[226px] before:bg-[#1f1f1f] z-40',
            content: 'text-sm text-white bg-[#1f1f1f] border-none rounded-xl px-4 py-3 select-none'
          }}
        >
          <div className='inline-flex'>{dropdownElement}</div>
        </Tooltip>
      ) : (
        dropdownElement
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type='file'
        multiple
        className='hidden'
        onChange={handleFilesChange}
      />
    </>
  )
}

export default AttachUploadButton
